import React, { useContext, useEffect, useRef, useState } from "react";
import {
  FaBars,
  FaChevronDown,
  FaExternalLinkAlt,
  FaGift,
  FaMoon,
  FaRegCopy,
  FaSun,
  FaTimes,
  FaUserCircle,
  FaUsers,
  FaWallet,
} from "react-icons/fa";
import { SiBinance } from "react-icons/si";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useActiveAccount, useActiveWallet, useConnectModal, useDisconnect } from "thirdweb/react";
import {
  thirdwebAppMetadata,
  thirdwebClient,
  thirdwebWalletConnectOptions,
  thirdwebWallets,
  bscChain,
} from "../config/thirdweb";
import { BRAND } from "../config/brand";
import { Store } from "../context/StoreContext";
import { useTheme } from "../context/ThemeContext";
import ReferralRewardBanner from "./ReferralRewardBanner";


const NAV_ITEMS = [
  { label: "About 1EYE", sectionId: "about" },
  { label: "Proof of Value", sectionId: "proof-of-value" },
  { label: "Tokenomics", sectionId: "tokenomics" },
  { label: "Wallets", sectionId: "wallets" },
  { label: "Roadmap", sectionId: "roadmap" },
  { label: "FAQs", sectionId: "faqs" },
];

const Navbar = () => {
  const [isActive, setIsActive] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [copiedField, setCopiedField] = useState("");
  const walletAreaRef = useRef(null);
  const mobileWalletAreaRef = useRef(null);
  const headerRef = useRef(null);
  const location = useLocation();
  const account = useActiveAccount();
  const wallet = useActiveWallet();
  const { connect } = useConnectModal();
  const { disconnect } = useDisconnect();
  const address = account?.address;
  const isConnected = Boolean(address);
  const { userDatabaseData } = useContext(Store);
  const { theme, toggleTheme } = useTheme();
  const logoSrc = theme === "light" ? BRAND.logoPathLight : BRAND.logoPath;
  let navigate = useNavigate();

  const handleConnectWallet = async () => {
    try {
      await connect({
        client: thirdwebClient,
        chain: bscChain,
        wallets: thirdwebWallets,
        appMetadata: thirdwebAppMetadata,
        walletConnect: thirdwebWalletConnectOptions,
      });
    } catch {
      // User closed the modal or connection failed; nothing further to do.
    }
  };

  const handleDisconnectWallet = () => {
    if (wallet) disconnect(wallet);
  };

  const shortAddress = (value) =>
    value ? `${value.slice(0, 6)}...${value.slice(-4)}` : "Not assigned";

  const referralProfile = {
    referredBy: userDatabaseData?.referredBy || null,
    referralAddress:
      userDatabaseData?.referralAddress ||
      userDatabaseData?.referralWallet ||
      address ||
      "",
    totalReferrals:
      userDatabaseData?.totalReferrals || userDatabaseData?.referralCount || 0,
    receivedAmount:
      userDatabaseData?.receivedReferralAmount ||
      userDatabaseData?.referralRewards ||
      "0.00",
  };

  useEffect(() => {
    const closeProfile = (event) => {
      const outsideDesktop = !walletAreaRef.current?.contains(event.target);
      const outsideMobile = !mobileWalletAreaRef.current?.contains(event.target);
      if (outsideDesktop && outsideMobile) {
        setIsProfileOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setIsProfileOpen(false);
    };

    document.addEventListener("pointerdown", closeProfile);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeProfile);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  // Lock background scroll while the full-screen mobile menu is open, and
  // always restore the previous value on close/unmount rather than assuming
  // it was "". Also flags <body> so other fixed-position UI (e.g. the
  // floating presale bar) can hide itself via CSS: some browsers order
  // independently-composited `position: fixed` layers (this app has several,
  // several using backdrop-filter) by paint/creation order rather than
  // z-index in edge cases, so z-index alone isn't reliable here — a
  // full-screen nav overlay shouldn't show other floating UI through/behind
  // it regardless.
  useEffect(() => {
    if (!isActive) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.body.classList.add("mobile-nav-open");

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.classList.remove("mobile-nav-open");
    };
  }, [isActive]);

  // Header is position: fixed, so page content needs a matching top offset
  // (--header-height, consumed by .site-shell and section[id] scroll-margin
  // in index.css). Measuring the real rendered height here instead of
  // hardcoding per-breakpoint guesses keeps it correct across font loading,
  // content/wallet-state changes, and any future header edits.
  useEffect(() => {
    const headerEl = headerRef.current;
    if (!headerEl || typeof ResizeObserver === "undefined") return undefined;

    const applyHeight = () => {
      document.documentElement.style.setProperty(
        "--header-height",
        `${headerEl.offsetHeight}px`,
      );
    };

    applyHeight();
    const observer = new ResizeObserver(applyHeight);
    observer.observe(headerEl);
    return () => observer.disconnect();
  }, []);

  const themeToggleButton = (
    <button
      type="button"
      className="theme-toggle-btn"
      onClick={toggleTheme}
      aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
      title={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
    >
      {theme === "dark" ? (
        <FaSun aria-hidden="true" />
      ) : (
        <FaMoon aria-hidden="true" />
      )}
    </button>
  );

  const copyValue = async (value, field) => {
    if (!value || value === "Not assigned") return;
    await navigator.clipboard.writeText(value);
    setCopiedField(field);
    window.setTimeout(() => setCopiedField(""), 1400);
  };

  const walletControls = (mobile = false) => (
    <div
      className={`nav-wallet ${mobile ? "nav-wallet--mobile" : ""}`}
      ref={mobile ? mobileWalletAreaRef : walletAreaRef}
    >
      {!isConnected ? (
        <button className="nav-connect-button" onClick={handleConnectWallet}>
          <FaWallet aria-hidden="true" />
          Connect Wallet
        </button>
      ) : (
        <>
          <div className="nav-chain-pill" title="Connected network">
            <span className="nav-chain-icon"><SiBinance aria-hidden="true" /></span>
            <span>BSC Chain</span>
          </div>
          <button
            className="nav-profile-trigger"
            onClick={() => setIsProfileOpen((current) => !current)}
            aria-expanded={isProfileOpen}
            aria-label="Open wallet profile"
          >
            <FaUserCircle className="nav-profile-icon" aria-hidden="true" />
            <span>{shortAddress(address)}</span>
            <FaChevronDown className="nav-profile-chevron" aria-hidden="true" />
          </button>
        </>
      )}

      {isConnected && isProfileOpen && (
        <div className="nav-profile-card">
          <div className="nav-profile-card__head">
            <div>
              <small>Your 1EYE profile</small>
              <strong>{shortAddress(address)}</strong>
            </div>
            <button onClick={() => copyValue(address, "wallet")} aria-label="Copy wallet address">
              <FaRegCopy aria-hidden="true" />
            </button>
          </div>

          <div className="nav-referral-grid">
            <div className="nav-referral-stat nav-referral-stat--wide">
              <span><FaWallet aria-hidden="true" /> Referred by</span>
              <strong>{shortAddress(referralProfile.referredBy)}</strong>
            </div>
            <div className="nav-referral-stat nav-referral-stat--wide">
              <span><FaRegCopy aria-hidden="true" /> Referral address</span>
              <strong>{shortAddress(referralProfile.referralAddress)}</strong>
              <button
                onClick={() => copyValue(`${window.location.origin}/buy?ref=${address}`, "referral")}
                aria-label="Copy referral address"
              >
                {copiedField === "referral" ? "Copied" : "Copy"}
              </button>
            </div>
            <div className="nav-referral-stat">
              <span><FaUsers aria-hidden="true" /> Total referrals</span>
              <strong>{referralProfile.totalReferrals}</strong>
            </div>
            <div className="nav-referral-stat">
              <span><FaGift aria-hidden="true" /> Rewards received</span>
              <strong>{referralProfile.receivedAmount} {BRAND.tokenSymbol}</strong>
            </div>
          </div>

          <button
            className="nav-manage-wallet"
            onClick={() => {
              setIsProfileOpen(false);
              handleDisconnectWallet();
            }}
          >
            Disconnect wallet
            <FaExternalLinkAlt aria-hidden="true" />
          </button>
          {copiedField === "wallet" && <span className="nav-copy-status">Wallet address copied</span>}
        </div>
      )}
    </div>
  );
  const scrollToSection = (id) => {
    const section = document.getElementById(id);
    if (section) {
      const headerHeight = document.querySelector("header")?.offsetHeight || 80;
      const bannerHeight = document.querySelector(".referral-reward-banner")?.offsetHeight || 0;
      const sectionTop = section.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: sectionTop - headerHeight - bannerHeight - 12,
        behavior: "smooth",
      });
    }
  };

  const handleNavigation = (id) => {
    if (location.pathname !== "/") {
      navigate(
        { pathname: "/", search: location.search },
        { state: { scrollToId: id } },
      );
    } else {
      scrollToSection(id);
    }
    setIsActive(false);
  };

  // const handleMenuClick = (id) => {
  //   if (location.pathname === "/") {
  //     // If on landing page, scroll to section
  //     scrollToSection(id);
  //   } else if (location.pathname == "/buy") {
  //     // If on the buy page, navigate to the section directly using hash
  //     navigate(`/#${id}`);
  //   }
  // };
  return (
    <>
      <header ref={headerRef}>
        <div className="left">
          <div className="logo-con">
            <Link to="/">
              <img src={logoSrc} alt={`${BRAND.name} logo`} className="logo" />
              <p>{BRAND.name}</p>
            </Link>
          </div>
          <ul className="menu-links">
            {NAV_ITEMS.map((item) => (
              <li key={item.sectionId}>
                <button onClick={() => handleNavigation(item.sectionId)}>
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="right">
          {walletControls()}
          {themeToggleButton}
        </div>

        {/* MOBILE MENU */}
        <div className="hamburger-mobile">
          <button
            type="button"
            className="hamburger"
            onClick={() => setIsActive((prev) => !prev)}
            aria-label="Open menu"
            aria-expanded={isActive}
          >
            <FaBars size={30} />
          </button>
        </div>

        <div
          className={`mobile-nav ${isActive ? "active" : ""}`}
          id="mobile-hambargur"
        >
          <div className="one">
            <div className="one-left">
              <Link to="/" onClick={() => setIsActive(false)}>
                <img src={logoSrc} alt={`${BRAND.name} logo`} className="logo" />
                <p>{BRAND.name}</p>
              </Link>
            </div>
            <button
              type="button"
              className={`one-right ${isActive ? "active" : ""}`}
              onClick={() => setIsActive(false)}
              aria-label="Close menu"
            >
              <FaTimes size={30} />
            </button>
          </div>

          <div className="two" id="after-login-mobile">
            <ul>
              {NAV_ITEMS.map((item) => (
                <li key={item.sectionId}>
                  <button onClick={() => handleNavigation(item.sectionId)}>
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
            <div className="button-con">
              {/* <button
              onClick={() => {
                navigate("/assettransparency"), setIsActive(false);
              }}
            >
              Asset Transparency
            </button> */}
              {themeToggleButton}
              {walletControls(true)}
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Navbar;
